import type {
  PickerOptionItem,
  PickerOptionsPagination,
  PickerOptionsResponse,
} from 'slices/aiConversations/pickerTypes'

import { api } from './index'

export interface GetPickerOptionsParams {
  // The endpoint path from picker config
  endpoint: string
  page?: number
  perPage?: number
  query?: string
  // Additional params from picker config (table_name, fields, search_field, filter_field, etc.)
  additionalParams?: Record<string, string | number | string[] | boolean>
  // Field mapping for response transformation
  keyField?: string
  valueField?: string
}

/**
 * Normalizes the endpoint path for use with the base URL.
 * Removes merchant path prefix since base URL already includes merchant context.
 * Ensures no leading slash to avoid double slashes with base URL.
 */
export function normalizeEndpoint(endpoint: string): string {
  let normalized = endpoint

  // Remove /api/v1/merchants/{id}/ prefix if present (id can be placeholder or number)
  const apiPrefixPattern = /^\/api\/v1\/merchants\/(:merchant_id|\d+)\//
  normalized = normalized.replace(apiPrefixPattern, '')

  // Also handle /merchants/{id}/ prefix (without /api/v1)
  const merchantPrefixPattern = /^\/merchants\/(:merchant_id|\d+)\//
  normalized = normalized.replace(merchantPrefixPattern, '')

  // Remove leading slash to avoid double slash with base URL
  if (normalized.startsWith('/')) {
    normalized = normalized.slice(1)
  }

  return normalized
}

/**
 * Checks if this is a snowflake query endpoint
 */
function isSnowflakeEndpoint(endpoint: string): boolean {
  return endpoint.includes('snowflake/query')
}

interface WhereCondition {
  field: string
  operator: string
  value: string | string[]
}

export interface SnowflakeQuery {
  tableName?: string
  distinct?: string
  fields?: string[]
  orderBy?: string[]
  where?: WhereCondition[]
}

/**
 * Builds URLSearchParams for the snowflake query endpoint.
 * Grape expects array params with [] suffix format and repeated keys for arrays:
 * - fields[]=value1&fields[]=value2
 * - where[][field]=x&where[][operator]=y&where[][value]=z (repeated for each condition)
 * - order_by[][field]=x&order_by[][direction]=asc
 *
 * Using URLSearchParams allows us to have multiple values for the same key.
 */
export function buildSnowflakeQueryParams(
  page: number,
  perPage: number,
  { tableName, distinct, fields = [], orderBy = [], where = [] }: SnowflakeQuery
): URLSearchParams {
  const params = new URLSearchParams()

  params.append('page', String(page))
  params.append('per_page', String(perPage))
  if (tableName) params.append('table_name', tableName)
  if (distinct !== undefined) params.append('distinct', distinct)
  fields.forEach(field => params.append('fields[]', field))
  orderBy.forEach(field => {
    params.append('order_by[][field]', field)
    params.append('order_by[][direction]', 'asc')
  })

  // Rack starts a new condition at each repeated `where[][field]`, and appends
  // an `in` condition's `where[][value][]` keys to the condition they follow.
  where.forEach(({ field, operator, value }) => {
    params.append('where[][field]', field)
    params.append('where[][operator]', operator)
    if (Array.isArray(value)) {
      value.forEach(item => params.append('where[][value][]', item))
    } else {
      params.append('where[][value]', value)
    }
  })

  return params
}

/**
 * Maps a picker config's additional params onto a snowflake query.
 */
function buildSnowflakeParams(
  page: number,
  perPage: number,
  query: string | undefined,
  additionalParams:
    | Record<string, string | number | string[] | boolean>
    | undefined
): URLSearchParams {
  if (!additionalParams) return buildSnowflakeQueryParams(page, perPage, {})

  // Order by the search_field, if there is one
  const searchField = additionalParams.search_field as string | undefined
  const filterField = additionalParams.filter_field as string | undefined
  const filterValue = additionalParams.filter_value as string | undefined
  const where: WhereCondition[] = []

  // Add filter condition if filter_field and filter_value provided
  if (filterField && filterValue && filterValue !== ':level_1') {
    where.push({ field: filterField, operator: '=', value: filterValue })
  }

  // Add search condition if query provided
  if (query && searchField) {
    where.push({ field: searchField, operator: 'like', value: `%${query}%` })
  }

  return buildSnowflakeQueryParams(page, perPage, {
    tableName: additionalParams.table_name
      ? String(additionalParams.table_name)
      : undefined,
    distinct:
      additionalParams.distinct === undefined
        ? undefined
        : String(additionalParams.distinct),
    fields: Array.isArray(additionalParams.fields)
      ? additionalParams.fields.map(String)
      : [],
    orderBy: searchField ? [searchField] : [],
    where,
  })
}

/**
 * Builds URLSearchParams for regular (non-snowflake) endpoints
 */
function buildRegularParams(
  page: number,
  perPage: number,
  query: string | undefined,
  additionalParams:
    | Record<string, string | number | string[] | boolean>
    | undefined
): URLSearchParams {
  const params = new URLSearchParams()

  params.append('page', String(page))
  params.append('per_page', String(perPage))

  if (query) {
    params.append('query', query)
  }

  // Add additional params (excluding picker-specific meta fields)
  if (additionalParams) {
    const excludeKeys = [
      'search_field',
      'filter_field',
      'filter_value',
      'fields',
      'distinct',
      'table_name',
    ]
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (!excludeKeys.includes(key) && typeof value !== 'object') {
        params.append(key, String(value))
      }
    })
  }

  return params
}

/**
 * Snowflake query response format
 */
interface SnowflakeResponse {
  records: Array<Record<string, string | number>>
  pagination: PickerOptionsPagination
  table_name: string
  distinct: boolean
}

/**
 * Regular API response format - data is in a dynamic key
 */
interface RegularApiResponse {
  [key: string]: PickerOptionItem[] | PickerOptionsPagination | undefined
  pagination?: PickerOptionsPagination
}

type RawApiResponse = SnowflakeResponse | RegularApiResponse

const hasPresence = (value: unknown): boolean =>
  value != null && String(value).trim() !== ''

/**
 * Builds a "street, city, state" label from a location row, dropping blank
 * components so a missing street doesn't produce a leading comma. Returns ''
 * when the row carries no address fields (non-location pickers).
 */
function formatLocationAddress(record: Record<string, unknown>): string {
  return [record.street, record.city, record.state]
    .map(part => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(', ')
}

/**
 * Maps raw records to PickerOptionItem[] using keyField/valueField mapping.
 * Used by the regular (non-snowflake) response path.
 */
export function mapRawItemsToPickerOptions(
  rawItems: Record<string, unknown>[],
  keyField?: string,
  valueField?: string
): PickerOptionItem[] {
  return rawItems.map(record => {
    let id: string
    let name: string

    if (keyField && record[keyField] != null) {
      id = String(record[keyField])
    } else if (record.id != null) {
      id = String(record.id)
    } else {
      const values = Object.values(record).filter(v => v != null)
      id = String(values[0] || '')
    }

    if (valueField && hasPresence(record[valueField])) {
      name = String(record[valueField]).trim()
    } else if (hasPresence(record.name)) {
      name = String(record.name).trim()
    } else {
      // Locations may have a blank name; fall back to the street address rather
      // than ever exposing the raw id. Rows without address fields (items,
      // modifiers, tags) keep the existing id fallback.
      name = formatLocationAddress(record) || id
    }

    return { id, name }
  })
}

export const pickerDataApi = api.injectEndpoints({
  endpoints: builder => ({
    /**
     * Generic endpoint for fetching picker options.
     * Handles both snowflake query and regular endpoints.
     */
    getPickerOptions: builder.query<
      PickerOptionsResponse,
      GetPickerOptionsParams
    >({
      query: ({
        endpoint,
        page = 1,
        perPage = 50,
        query,
        additionalParams,
      }) => {
        const normalizedUrl = normalizeEndpoint(endpoint)
        const isSnowflake = isSnowflakeEndpoint(normalizedUrl)

        const searchParams = isSnowflake
          ? buildSnowflakeParams(page, perPage, query, additionalParams)
          : buildRegularParams(page, perPage, query, additionalParams)

        // Append URLSearchParams to URL manually to preserve repeated keys
        // (fetchBaseQuery's params option doesn't handle repeated keys properly)
        const queryString = searchParams.toString()
        const urlWithParams = queryString
          ? `${normalizedUrl}?${queryString}`
          : normalizedUrl

        return {
          url: urlWithParams,
        }
      },
      // Transform the response to a consistent format
      transformResponse: (
        response: RawApiResponse,
        _meta,
        arg: GetPickerOptionsParams
      ): PickerOptionsResponse => {
        // Get field mapping from query args (defaults to using first value)
        const { keyField, valueField } = arg

        // Check if this is a snowflake response (has 'records' key)
        if ('records' in response && Array.isArray(response.records)) {
          const snowflakeResponse = response as SnowflakeResponse
          // Transform snowflake records to picker items
          // Records are like { item_name: "Burger" } or { modifier_name: "Cheese" }
          const items: PickerOptionItem[] = snowflakeResponse.records.map(
            record => {
              // Use keyField and valueField if provided, otherwise use first value
              let id: string
              let name: string

              if (keyField && record[keyField] != null) {
                id = String(record[keyField])
              } else {
                // Fallback: use first non-null value
                const values = Object.values(record).filter(v => v != null)
                id = String(values[0] || '')
              }

              if (valueField && record[valueField] != null) {
                name = String(record[valueField])
              } else {
                // Fallback: use same as id
                name = id
              }

              return { id, name }
            }
          )

          return {
            items,
            pagination: snowflakeResponse.pagination,
          }
        }

        // Regular response - find the array in the response
        const regularResponse = response as RegularApiResponse
        const itemsKey = Object.keys(regularResponse).find(
          key => Array.isArray(regularResponse[key]) && key !== 'pagination'
        )

        const rawItems = itemsKey
          ? (regularResponse[itemsKey] as Record<string, unknown>[])
          : []

        // Apply keyField/valueField mapping (same logic as snowflake path)
        // e.g., user_tag_keys returns { value, label } but picker expects { id, name }
        const items = mapRawItemsToPickerOptions(rawItems, keyField, valueField)

        return {
          items,
          pagination: regularResponse.pagination,
        }
      },
      // Don't cache picker options since they may change based on context
      providesTags: ['PickerOptions'],
    }),
  }),
})

export const { useGetPickerOptionsQuery, useLazyGetPickerOptionsQuery } =
  pickerDataApi
